import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router';
import {fromJS} from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import classnames from 'classnames';
import moment from 'moment';
import {useTracking} from 'react-tracking';
import {MEGA_CLASSES_SIZE} from 'client/constants';

import {Text, LoadingSpinner} from '@albert-io/atomic';
import {BUCKETS as statsBuckets} from 'lib/StatsUtil';
import {humanReadableDurationString} from 'lib/timeUtils';
import IconTooltip from 'sg/IconTooltip/IconTooltip.react';
import {StudentModelV2} from 'resources/GeneratedModels/Student/StudentModel.v2';
import SimpleDropdown from 'generic/SimpleDropdown/SimpleDropdown.react';
import {getModelForResourceType} from 'resources/modelRegistry';

import AutoArchiveClassroomBanner from 'client/Classrooms/AutoArchiveClassrooms/AutoArchiveClassroomsBanner.react';

import TeacherClassroomScope from '../TeacherClassroomScope/TeacherClassroomScope.react';
import classroomV2Store from '../../ClassroomV2.store';

import EmptyState from '../EmptyState/index.react';
import MegaClassesEmptyState from '../MegaClassesEmptyState/MegaClassesEmptyState.react';

import './teacher-dashboard.scss';

const TeacherDashboard = ({classroom}) => {
  const {Track} = useTracking({
    feature: 'Overview'
  });

  if (classroom.getMeta().getCountOfStudents() > MEGA_CLASSES_SIZE) {
    return <MegaClassesEmptyState supportingTitleText='Overview data will not load' />;
  }

  if (classroom.getSubjects().isEmpty()) {
    return (
      <Track>
        <EmptyState
          title='No overview data'
          body='Classroom data will appear when students answer questions. Visit the library to assign questions.'
        />
      </Track>
    );
  }
  return (
    <div className='teacher-dashboard u-max-width_legacy u-mar-lr_auto u-mar-tb_0'>
      <TeacherClassroomOverview key={classroomV2Store.getActiveClassroomId()} />
      <SortBy />
      <StudentColumns />
      <AutoArchiveClassroomBanner />
    </div>
  );
};

TeacherDashboard.propTypes = {
  classroom: PropTypes.instanceOf(getModelForResourceType('classroom_v1'))
};

export default TeacherDashboard;

function TimedLoadingSpinner() {
  const [isTakingAwhile, setIsTakingAwhile] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsTakingAwhile(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className='u-mar-t_7 u-display_flex u-flex-direction_column'>
      <LoadingSpinner size={2} />
      {isTakingAwhile ? (
        <Text as='div' className='u-mar-lr_auto u-mar-tb_0 u-text-align_center' emphasis='low'>
          Wow, that&apos;s a lot of data! One moment, please…
        </Text>
      ) : null}
    </div>
  );
}

function StudentColumns() {
  if (!classroomV2Store.areStudentsReady()) {
    return <TimedLoadingSpinner />;
  }
  const students = classroomV2Store.getStudents();
  return (
    <div className='teacher-dashboard__columns-wrapper'>
      <StudentColumn
        heading={statsBuckets.STRUGGLING.DISPLAY_NAME}
        students={students.getStrugglingStudents()}
      />
      <StudentColumn
        heading={statsBuckets.PASSING.DISPLAY_NAME}
        students={students.getPassingStudents()}
      />
      <StudentColumn
        heading={statsBuckets.PROFICIENT.DISPLAY_NAME}
        students={students.getProficientStudents()}
      />
      <StudentColumn
        heading={statsBuckets.EXCELLING.DISPLAY_NAME}
        students={students.getExcellingStudents()}
      />
      <StudentColumn
        heading={statsBuckets.MASTERY.DISPLAY_NAME}
        students={students.getMasteryStudents()}
      />
    </div>
  );
}

const TeacherClassroomOverview = () => {
  const activeClassroom = classroomV2Store.getActiveClassroom();
  const areStudentsReady = classroomV2Store.areStudentsReady();
  let students;
  let totalTimeElapsedMoment;
  if (areStudentsReady) {
    students = classroomV2Store.getStudents();
    totalTimeElapsedMoment = moment.duration(students.getTotalTimeElapsed(), 'milliseconds');
  }
  return (
    <div className='teacher-classroom-overview card'>
      <h1 className='teacher-classroom-overview__classroom-name'>{activeClassroom.getName()}</h1>
      <TeacherClassroomScope className='teacher-classroom-overview__scope' />
      <div className='teacher-classroom-overview__stats-wrapper'>
        <TeacherClassroomOverviewStat
          count={areStudentsReady ? students.getActiveStudentsCount() : '—'}
          name='active students'
        />
        <TeacherClassroomOverviewStat
          count={areStudentsReady ? students.getInactiveStudentsCount() : '—'}
          name='inactive students'
        />
        <div className='teacher-classroom-overview__stat-divider' />
        <TeacherClassroomOverviewStat
          count={areStudentsReady ? students.getTotalNumberOfAttempts() : '—'}
          name='questions answered'
        />
        <TeacherClassroomOverviewStat
          count={areStudentsReady ? students.getTotalPointsEarned() : '—'}
          name='points earned'
          tooltip={
            <IconTooltip
              icon='fa-info-circle'
              content={[
                'Students earn points by answering questions correctly. ' +
                  'Questions are assigned points based on their difficulty.'
              ]}
            />
          }
        />
        <TeacherClassroomOverviewStat
          count={areStudentsReady ? `${students.getActiveStudentsAverageAccuracy()}%` : '—'}
          name='average accuracy'
        />
        <div className='teacher-classroom-overview__stat-divider' />
        <TeacherClassroomOverviewStat
          count={areStudentsReady ? humanReadableDurationString(totalTimeElapsedMoment) : '—'}
          name='time spent'
        />
      </div>
    </div>
  );
};

class TeacherClassroomOverviewStat extends React.PureComponent {
  static propTypes = {
    className: PropTypes.string,
    count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    tooltip: PropTypes.node
  };

  render() {
    return (
      <div className={classnames('teacher-classroom-overview-stat', this.props.className)}>
        <div className='teacher-classroom-overview-stat__count'>{this.props.count}</div>
        <div className='teacher-classroom-overview-stat__name'>
          {this.props.name}
          {this.props.tooltip ? this.props.tooltip : null}
        </div>
      </div>
    );
  }
}

class SortBy extends React.Component {
  dropdownOptions = fromJS([
    {
      id: 0,
      name: 'Student name',
      value: 'first_name'
    },
    {
      id: 1,
      name: 'Total questions',
      value: '-meta.count_of_guesses'
    },
    {
      id: 2,
      name: 'Raw accuracy',
      // This is not a field Mandark can currently sort by,
      // so we'll fake it in the mean time. See ClassroomV2.store and
      // search for raw_accuracy for fake implementation
      value: 'raw_accuracy'
    }
  ]);

  render() {
    return (
      <div className='teacher-classroom-dashboard-sort-by'>
        Sort by
        <SimpleDropdown
          className='teacher-classroom-dashboard-sort-by__dropdown'
          storeName={classroomV2Store.getTeacherClassroomSortByDropdownStoreName()}
          options={this.dropdownOptions}
        />
      </div>
    );
  }
}

class StudentColumn extends React.PureComponent {
  static propTypes = {
    heading: PropTypes.string,
    students: ImmutablePropTypes.listOf(PropTypes.instanceOf(StudentModelV2))
  };

  render() {
    const {heading} = this.props;
    // Using the heading to create a modifier class in order to set the correct accent color in the stylesheet
    const wrapperClass = `teacher-dashboard-student-column teacher-dashboard-student-column--${heading.toLowerCase()}`;
    const students = this.props.students.isEmpty() ? (
      <div className='teacher-dashboard-student-column__no-students-found'>No students found</div>
    ) : (
      <ul className='teacher-dashboard-student-column__list'>
        {this.props.students.map((student) => {
          return <Student student={student} key={student.getId()} />;
        })}
      </ul>
    );
    return (
      <div className={wrapperClass}>
        <div className='teacher-dashboard-student-column__heading'>{heading}</div>
        {students}
      </div>
    );
  }
}

class Student extends React.PureComponent {
  static propTypes = {
    student: PropTypes.instanceOf(StudentModelV2)
  };

  render() {
    const {student} = this.props;
    return (
      <li className='teacher-dashboard-student-item card card--hover'>
        <Link
          className='teacher-dashboard-student-item__link'
          to={`/classes/${classroomV2Store.getActiveClassroomId()}/students/detail/${student.getId()}`}
        >
          <div className='teacher-dashboard-student-item__name'>{student.getFullName()}</div>
          <div className='teacher-dashboard-student-item__stat'>
            <span>{student.getMeta().getCountOfGuesses()}</span> <span>questions</span>
          </div>
          <div className='teacher-dashboard-student-item__divider' />
          <div className='teacher-dashboard-student-item__stat'>
            <span>{student.getAccuracyFromMeta()}%</span> <span>raw accuracy</span>
          </div>
        </Link>
      </li>
    );
  }
}
